import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';

const Blog = () => {
  const { toast } = useToast();

  const posts = [
    {
      id: 1,
      title: 'Why Structured Cabling is the Foundation of Your Office Network',
      excerpt: 'A well-planned cabling infrastructure reduces downtime, simplifies troubleshooting, and scales with your business. Here\'s what to consider before your next office build-out.',
      author: 'Glenn R.',
      date: 'March 10, 2026',
      category: 'Infrastructure',
    },
    {
      id: 2,
      title: 'Data Center Migration: How to Avoid the Most Common Pitfalls',
      excerpt: 'From compatibility gaps to policy mapping errors, data center migrations carry real risk. We break down the most common mistakes and how our staged rollout approach mitigates them.',
      author: 'Earlwin C.',
      date: 'February 28, 2026',
      category: 'Data Center',
    },
    {
      id: 3,
      title: 'Next-Gen Firewall vs. Traditional Firewall: What Enterprises Need to Know',
      excerpt: 'Security perimeters have changed. We compare NGFW capabilities against legacy hardware and explain why VLANs, ACLs, and segmentation matter more than ever in 2026.',
      author: 'Lawrence C.',
      date: 'February 15, 2026',
      category: 'Security',
    },
    {
      id: 4,
      title: 'Building the Perfect Meeting Room: AV Essentials for Enterprise',
      excerpt: 'Great meetings start with great AV. We cover display sizing, speaker placement, acoustic treatment, and control system integration for boardrooms and huddle spaces.',
      author: 'Glenn R.',
      date: 'January 30, 2026',
      category: 'AV Solutions',
    },
    {
      id: 5,
      title: 'Microsoft Teams Rooms vs. Zoom Rooms: Which Is Right for Your Business?',
      excerpt: 'Both platforms offer enterprise-grade conferencing, but the right choice depends on your existing stack, room size, and user workflows. Here\'s our side-by-side breakdown.',
      author: 'Earlwin C.',
      date: 'January 18, 2026',
      category: 'Conferencing',
    },
    {
      id: 6,
      title: 'IT Equipment Warehousing: Why Asset Tagging Matters from Day One',
      excerpt: 'Proper asset management starts before equipment is deployed. We explore how structured receiving, climate control, and asset tagging reduce loss, waste, and audit headaches.',
      author: 'Lawrence C.',
      date: 'January 5, 2026',
      category: 'Warehousing',
    },
  ];

  const handleReadMore = (title) => {
    toast({
      title: "🚧 This feature isn't implemented yet—but don't worry! You can request it in your next prompt! 🚀",
    });
  };

  return (
    <>
      <Helmet>
        <title>Blog - Tasks IT Services | IT Infrastructure Insights & Best Practices</title>
        <meta name="description" content="Industry insights, best practices, and technical guides on data center migration, network infrastructure, AV solutions, and enterprise IT from the Tasks IT Services team." />
      </Helmet>

      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl font-bold mb-6">
              IT Infrastructure <span className="gradient-text">Insights</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert guides, best practices, and industry perspectives from the Tasks IT Services engineering team
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="h-48 bg-gradient-to-br from-blue-600 to-cyan-500"></div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold mb-3 line-clamp-2">{post.title}</h2>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{post.date}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => handleReadMore(post.title)}
                    variant="outline" 
                    className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    Read More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Blog;